import type { InferContractRouterOutputs } from "@orpc/contract";
import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  leadTimeProfileSchema,
  leadTimeComponentSourceSchema,
  leadTimeComponentOverrideSchema,
} from "@erp/shared-types";
import {
  OverrideWindowShape,
  withOverrideWindowRules,
} from "./override-window";

const target = { productV3Key: z.string().min(1), warehouseId: z.uuid() };
const settingsResponse = z.object({
  stalePolicyCount: z.number().nonnegative(),
  profile: leadTimeProfileSchema.nullable(),
  fingerprint: z.string(),
});
const changeResponse = z.object({
  fingerprint: z.string(),
  recomputeRequired: z.literal(true),
});
export const leadTimeSettingsContracts = {
  get: oc.output(settingsResponse).route({
    method: "GET",
    summary: "Read the active vendor lead-time profile",
    description:
      "Read organization-wide vendor-buy lead time component defaults, the current settings fingerprint, and the count of policies requiring recomputation. A missing profile preserves legacy estimates.",
  }),
  save: oc
    .input(
      z.object({
        profile: leadTimeProfileSchema.nullable(),
        expectedFingerprint: z.string(),
      }),
    )
    .output(changeResponse)
    .route({
      method: "POST",
      summary:
        "Explicitly activate or clear the profile for future calculations",
      description:
        "Save or clear the organization-wide component profile using the expected fingerprint to reject stale edits. Requires organization configuration update permission. Changes apply to future calculations; existing policies and suggestions require recomputation.",
    }),
  preview: oc
    .input(z.object({ profile: leadTimeProfileSchema.nullable() }))
    .output(
      z.object({
        eligible: z.number(),
        excluded: z.record(z.string(), z.number()),
        beyondCoverage: z.number(),
        unavailable: z.number(),
        examples: z.array(
          z.object({
            ...target,
            beforeDays: z.number(),
            afterDays: z.number(),
            status: z.string(),
            coverageDays: z.number().nullable(),
            safetyStockDelta: z.number().nullable(),
            reorderPointDelta: z.number().nullable(),
            maxQuantityDelta: z.number().nullable(),
          }),
        ),
      }),
    )
    .route({
      method: "POST",
      summary:
        "Preview lead-time changes and sourcing exclusions without saving",
      description:
        "Return a best-effort preview against saved policy inputs and current component overrides, including eligibility, sourcing exclusions, coverage, and policy-level deltas with up to 30 examples. Missing inputs are counted as unavailable. Does not save settings or recompute results.",
    }),
  listOverrides: oc
    .input(z.object(target))
    .output(z.object({ overrides: z.array(leadTimeComponentOverrideSchema) }))
    .route({
      method: "GET",
      summary: "Component override audit history for one SKU and warehouse",
      description:
        "List audited component source overrides for one organization-scoped SKU and warehouse, including source, author, reason, activation window, review date, and revocations. Retains inactive and scheduled history.",
    }),
  saveOverride: oc
    .input(
      withOverrideWindowRules(
        z.object({
          ...target,
          componentId: z.string().min(1),
          source: leadTimeComponentSourceSchema,
          reason: z.string().trim().min(1).max(2000),
          supersedesOverrideId: z.uuid().nullable().optional(),
          ...OverrideWindowShape,
        }),
      ),
    )
    .output(changeResponse)
    .route({
      method: "POST",
      summary: "Save or replace an audited component source override",
      description:
        "Create a Static or supported Calculated source override for one profile component at a SKU and warehouse, with a reason and lifecycle window. An immediate replacement can supersede a prior override. Requires inventory update permission and applies to future calculations; saved results require recomputation.",
    }),
  revokeOverride: oc
    .input(z.object({ overrideId: z.uuid() }))
    .output(changeResponse)
    .route({
      method: "POST",
      summary: "Revoke an audited component source override",
      description:
        "Deactivate a component override while preserving its audit history. Future calculations use the next active override or inherit the organization profile source. Requires inventory update permission; saved results require recomputation.",
    }),
} as const;

export type LeadTimeSettingsPreviewResponse = InferContractRouterOutputs<
  typeof leadTimeSettingsContracts
>["preview"];
